import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { MobileNav } from '../components/MobileNav';
export function LifeDecode() {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handlePurchase = () => {
    // For testing: skip payment and go directly to access page
    // TODO: Replace with actual payment flow later
    navigate('/decode/life/access');
  };
  return <>
      <Helmet data-id="element-2259">
        <title data-id="element-2260">Life Decode | Lazy Monk Labs</title>
        <meta name="description" content="See the full structure you've been living inside. A Life Decode maps how your pattern shows up across time." data-id="element-2261" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2262">
        <MobileNav data-id="element-2263" />

        <main className="pt-20 sm:pt-24 pb-24 sm:pb-32" data-id="element-2264">
          <div className="max-w-2xl mx-auto px-6 sm:px-8" data-id="element-2265">
            {/* HEADER */}
            <header className="py-16 sm:py-20" data-id="element-2266">
              <h1 className="text-3xl sm:text-4xl leading-tight text-[#F5F5F5] mb-4" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2267">
                Life Decode
              </h1>
              <p className="text-sm text-gray-500" data-id="element-2268">
                Seeing the full structure you've been living inside.
              </p>
            </header>

            {/* OPENING — RECOGNITION, NOT EXPLANATION */}
            <section className="space-y-6 text-base sm:text-lg text-gray-400 leading-[1.7]" data-id="element-2269">
              <p data-id="element-2270">You've just seen a pattern that describes how you operate.</p>
              <p data-id="element-2271">Not a mood. Not a phase. A structure.</p>
              <p data-id="element-2272">
                Seeing it once helps. It puts words to something you've sensed
                for a long time — but couldn't quite place.
              </p>
              <p data-id="element-2273">
                But this structure didn't appear suddenly. And it hasn't been
                shaping only this moment.
              </p>
            </section>

            {/* DEPTH — INEVITABILITY FRAME */}
            <section className="pt-16 sm:pt-20" data-id="element-2274">
              <p className="text-base sm:text-lg text-[#F5F5F5] leading-[1.7] mb-6" data-id="element-2275">
                The same pattern you just read has likely been at work across
                your life:
              </p>
              <div className="space-y-3 text-base text-gray-400 leading-[1.7]" data-id="element-2276">
                <p data-id="element-2277">– in how responsibility entered early</p>
                <p data-id="element-2278">– in why certain choices felt inevitable</p>
                <p data-id="element-2279">– in why expansion was followed by correction</p>
                <p data-id="element-2280">– in why clarity arrived before rhythm</p>
              </div>
              <div className="pt-8 space-y-6 text-base sm:text-lg text-gray-400 leading-[1.7]" data-id="element-2281">
                <p data-id="element-2282">
                  A Life Decode looks at that entire arc — how the same
                  structure shows up across lineage, environment, life stages,
                  and phase.
                </p>
                <p data-id="element-2283">
                  Not to motivate you. To explain why your life could not have
                  unfolded very differently.
                </p>
              </div>
            </section>

            {/* WHAT THIS ACTUALLY IS */}
            <section className="pt-16 sm:pt-20" data-id="element-2284">
              <h2 className="text-lg sm:text-xl text-[#F5F5F5] mb-6" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2285">
                What this actually is
              </h2>
              <p className="text-base text-gray-400 leading-[1.7] mb-6" data-id="element-2286">
                This is not an extended personality read.
              </p>
              <p className="text-base text-gray-400 leading-[1.7] mb-6" data-id="element-2287">
                A Life Decode is a long-form structural map that shows:
              </p>
              <div className="space-y-3 text-base text-gray-400 leading-[1.7]" data-id="element-2288">
                <p data-id="element-2289">– how your core architecture formed</p>
                <p data-id="element-2290">– the generational pressures you inherited</p>
                <p data-id="element-2291">– the environments that shaped your pace and direction</p>
                <p data-id="element-2292">
                  – the phases where this structure expanded, corrected, and
                  settled
                </p>
                <p data-id="element-2293">– what this phase is no longer willing to support</p>
              </div>
              <p className="text-base text-[#F5F5F5] leading-[1.7] mt-8" data-id="element-2294">
                Most insight tools stop at recognition. This one continues until
                the trade-offs are impossible to ignore.
              </p>
            </section>

            {/* WHY PEOPLE FIND THIS UNSETTLING (AND USEFUL) */}
            <section className="pt-16 sm:pt-20" data-id="element-2295">
              <h2 className="text-lg sm:text-xl text-[#F5F5F5] mb-6" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2296">
                Why people find this unsettling (and useful)
              </h2>
              <div className="space-y-6 text-base text-gray-400 leading-[1.7]" data-id="element-2297">
                <p data-id="element-2298">People don't finish this decode feeling inspired.</p>
                <p data-id="element-2299">They finish it feeling oriented.</p>
                <p data-id="element-2300">
                  Confusion reduces. Urgency softens. Patterns stop feeling
                  personal — and start feeling structural.
                </p>
                <p className="text-[#F5F5F5]" data-id="element-2301">
                  Once seen at this level, the pattern doesn't disappear. It
                  becomes something you own.
                </p>
              </div>
            </section>

            {/* IS THIS FOR YOU? — COLLAPSIBLE FILTER */}
            <section className="pt-16 sm:pt-20" data-id="element-2302">
              <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="text-sm text-gray-500 hover:text-gray-400 transition-colors flex items-center gap-2" data-id="element-2303">
                Is this for you?
                <svg className={`w-3 h-3 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-2304">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" data-id="element-2305" />
                </svg>
              </button>

              {isFilterOpen && <div className="pt-6 opacity-100 transition-opacity duration-200" data-id="element-2306">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12" data-id="element-2307">
                    {/* THIS IS FOR YOU IF */}
                    <div className="space-y-3" data-id="element-2308">
                      <div className="flex items-start gap-3" data-id="element-2309">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2310">○</span>
                        <p className="text-sm text-gray-400" data-id="element-2311">
                          You already sensed the pattern
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2312">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2313">○</span>
                        <p className="text-sm text-gray-400" data-id="element-2314">
                          You want to see it across your whole life
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2315">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2316">○</span>
                        <p className="text-sm text-gray-400" data-id="element-2317">
                          You prefer clarity over reassurance
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2318">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2319">○</span>
                        <p className="text-sm text-gray-400" data-id="element-2320">
                          You're okay reading something dense and quiet
                        </p>
                      </div>
                    </div>

                    {/* THIS IS NOT FOR YOU IF */}
                    <div className="space-y-3" data-id="element-2321">
                      <div className="flex items-start gap-3" data-id="element-2322">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2323">—</span>
                        <p className="text-sm text-gray-500" data-id="element-2324">
                          You want steps or solutions
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2325">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2326">—</span>
                        <p className="text-sm text-gray-500" data-id="element-2327">
                          You're looking for predictions
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2328">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2329">—</span>
                        <p className="text-sm text-gray-500" data-id="element-2330">
                          You want something quick or light
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-2331">
                        <span className="text-gray-600 text-sm mt-0.5" data-id="element-2332">—</span>
                        <p className="text-sm text-gray-500" data-id="element-2333">
                          You're not in a reflective phase
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CLOSING LINE */}
                  <p className="text-xs text-gray-600 mt-8 text-center" data-id="element-2334">
                    This decode doesn't try to change you. It helps you see what
                    has already been shaping you.
                  </p>
                </div>}
            </section>

            {/* PRICE — RECONTEXTUALISED */}
            <section className="pt-12 sm:pt-16" data-id="element-2335">
              <p className="text-3xl sm:text-4xl text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2336">
                ₹999
              </p>
              <p className="text-sm text-gray-500 mt-2" data-id="element-2337">
                Launch price · Full Life Decode
              </p>
              <p className="text-xs text-gray-600 mt-1" data-id="element-2338">Standard price ₹2499</p>
              <p className="text-sm text-gray-500 mt-6 leading-relaxed" data-id="element-2339">
                This is a dense, long-form decode. Most people read it in parts
                and revisit it over time.
              </p>
            </section>

            {/* PRIMARY CTA */}
            <section className="pt-10 sm:pt-12" data-id="element-2340">
              <button onClick={handlePurchase} className="px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition-colors" data-id="element-2341">
                View Life Decode
              </button>
            </section>

            {/* EXIT */}
            <section className="pt-10 sm:pt-12" data-id="element-2342">
              <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-gray-500 transition-colors" data-id="element-2343">
                Not now
              </button>
            </section>
          </div>
        </main>
      </div>
    </>;
}